import { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, FormControl, FormHelperText, FormLabel,
    Input, Modal, ModalDialog, Stack, Typography,
} from '@mui/joy';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import { useAdvertiser } from '../../AdvertiserContext';
import { APIAdsDepositCreate, adsErrorText } from '../../api_ads';
import { formatMoney } from './adsConstants';


const PRESETS = [1000, 3000, 5000, 10000, 30000];


/**
 * Пополнение рекламного счёта.
 *
 * После создания платежа уводим на форму ЮKassa через window.location:
 * открытие в новой вкладке ломает возврат — пользователь оплачивает,
 * его возвращают в новую вкладку, а в старой по-прежнему старый баланс.
 */
export default function AdDepositModal({ open, onClose, account, limits, suggestedAmount }) {
    const { advertiserToken: token, advertiser } = useAdvertiser();

    const [amount, setAmount] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const minDeposit = Number(limits?.min_deposit || 0);
    const maxDeposit = Number(limits?.max_deposit || 500000);

    useEffect(() => {
        if (!open) return;
        setAmount(suggestedAmount ? String(suggestedAmount) : String(minDeposit || 1000));
        setEmail(account?.contact_email || advertiser?.email || '');
        setPhone(account?.contact_phone || advertiser?.phone || '');
        setError(null);
    }, [open, suggestedAmount, minDeposit, account]);

    const presets = useMemo(
        () => PRESETS.filter((value) => value >= minDeposit && value <= maxDeposit),
        [minDeposit, maxDeposit],
    );

    const submit = async () => {
        const value = Number(amount);
        if (!value || value < minDeposit) {
            setError(`Минимальная сумма пополнения — ${formatMoney(minDeposit)} ₽`);
            return;
        }
        if (value > maxDeposit) {
            setError(`Максимальная сумма одного пополнения — ${formatMoney(maxDeposit)} ₽`);
            return;
        }
        if (!email.trim() && !phone.trim()) {
            setError('Для чека нужен email или телефон');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            const res = await APIAdsDepositCreate(token, {
                amount: value,
                payerEmail: email.trim() || undefined,
                payerPhone: phone.trim() || undefined,
            });
            if (!res?.payment_url) {
                setError('Платёжная система не вернула ссылку на оплату. Попробуйте позже.');
                setSubmitting(false);
                return;
            }
            // Уходим на форму оплаты в этой же вкладке — так возврат
            // попадёт туда, откуда пользователь начал.
            window.location.href = res.payment_url;
        } catch (e) {
            setError(adsErrorText(e, 'Не удалось создать платёж'));
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={submitting ? undefined : onClose}>
            <ModalDialog sx={{ width: { xs: '94vw', sm: 460 } }}>
                <Typography level="title-lg">Пополнение рекламного счёта</Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                    Текущий баланс: <b>{formatMoney(account?.balance)} ₽</b>
                </Typography>

                {error && <Alert color="danger" sx={{ mb: 1.5 }}>{error}</Alert>}

                <Stack spacing={1.5}>
                    <Box>
                        <FormLabel sx={{ mb: 0.8 }}>Быстрый выбор</FormLabel>
                        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                            {presets.map((value) => (
                                <Chip
                                    key={value}
                                    variant={Number(amount) === value ? 'solid' : 'outlined'}
                                    color={Number(amount) === value ? 'primary' : 'neutral'}
                                    onClick={() => setAmount(String(value))}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    {formatMoney(value)} ₽
                                </Chip>
                            ))}
                        </Stack>
                    </Box>

                    <FormControl>
                        <FormLabel>Сумма, ₽</FormLabel>
                        <Input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                        />
                        <FormHelperText>
                            от {formatMoney(minDeposit)} до {formatMoney(maxDeposit)} ₽ за одну операцию
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel>Email для чека</FormLabel>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="name@example.com"
                        />
                        <FormHelperText>
                            Чек по 54-ФЗ формирует ЮKassa и отправляет на этот адрес
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel>Телефон для чека</FormLabel>
                        <Input
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+7 900 000-00-00"
                        />
                        <FormHelperText>Достаточно указать email или телефон</FormHelperText>
                    </FormControl>

                    <Alert color="neutral" variant="soft" size="sm">
                        После нажатия вы перейдёте на защищённую страницу ЮKassa.
                        Деньги зачислятся автоматически сразу после оплаты.
                    </Alert>

                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            color="neutral"
                            fullWidth
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Отмена
                        </Button>
                        <Button
                            fullWidth
                            loading={submitting}
                            onClick={submit}
                            endDecorator={<OpenInNewRoundedIcon />}
                        >
                            Перейти к оплате
                        </Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}